import { lazy, FC, Suspense } from 'react';
import { Route, Routes, Navigate } from 'react-router-dom';
import { MasterLayout } from '../../_metronic/layout/MasterLayout';
import TopBarProgress from 'react-topbar-progress-indicator';
import { HomeWrapper } from '../pages/home/HomeWrapper';
import { DashboardWrapper } from '../pages/dashboard/DashboardWrapper';
import { MenuTestPage } from '../pages/MenuTestPage';
import { getCSSVariableValue } from '../../_metronic/assets/ts/_utils';
import { useAuth } from '../modules/auth';
import { roleGates } from '../modules/auth/roleGates';
import { ClientNotes } from '../modules/apps/clients/client-details/ClientNotes';
import { ClientDocuments } from '../modules/apps/clients/client-details/ClientDocuments';
import { ClientContactInfo } from '../modules/apps/clients/client-details/ContactInfo';

// Module level: lazy() inside a component's render returns a new component type on every render, which remounts
// the routed page (and loses e.g. a half-filled form) whenever PrivateRoutes re-renders.
const BuilderPageWrapper = lazy(() => import('../pages/layout-builder/BuilderPageWrapper'));
const ProfilePage = lazy(() => import('../modules/profile/ProfilePage'));
const WizardsPage = lazy(() => import('../modules/wizards/WizardsPage'));
const AccountPage = lazy(() => import('../modules/accounts/AccountPage'));
const WidgetsPage = lazy(() => import('../modules/widgets/WidgetsPage'));
const ChatPage = lazy(() => import('../modules/apps/chat/ChatPage'));
const UsersPage = lazy(() => import('../modules/apps/user-management/UsersPage'));
const ServicesPage = lazy(() => import('../modules/apps/services/ServicesPage'));
const AvailabilityPage = lazy(() => import('../modules/apps/availability/AvailabilityPage'));
const AppsHubPage = lazy(() => import('../modules/apps/apps-hub/AppsHubPage'));
//const IntegrationsPage = lazy(() => import('../modules/apps/integrations/IntegrationsPage'));
//const PreferencesPage = lazy(() => import('../modules/apps/preferences/PreferencesPage'));
const ClientsPage = lazy(() => import('../modules/apps/clients/ClientsPage'));
const ClientDetailsPage = lazy(() => import('../modules/apps/clients/ClientDetailsPage'));
const ProvidersPage = lazy(() => import('../modules/apps/providers/ProvidersPage'));
const ProviderDetailsPage = lazy(() => import('../modules/apps/providers/ProviderDetailsPage'));
const ProviderDocuments = lazy(() => import('../modules/apps/providers/ProviderDocuments'));
const SchedulePage = lazy(() => import('../modules/apps/schedule/SchedulePage'));
const BookingPage = lazy(() => import('../modules/apps/booking/BookingPage'));
const FinancialsPage = lazy(() => import('../modules/apps/financials/FinancialsPage'));
const ReportsPage = lazy(() => import('../modules/apps/financials/ReportsPage'));
const ClinicPage = lazy(() => import('../modules/apps/providers/ClinicPage'));
const ReferralsLegacyPage = lazy(() => import('../modules/apps/referrals/ReferralsPage'));
const ReferralsPageV2 = lazy(() => import('../modules/apps/referrals/ReferralsPageV2'));
const ReferralProvidersPage = lazy(() => import('../modules/apps/referrals/ReferralProvidersPage'));
const ReferralDetailsPage = lazy(() => import('../modules/apps/referrals/ReferralDetails'));
const HomePage = lazy(() => import('../modules/apps/home/HomePage'));
const SetupPage = lazy(() => import('../modules/apps/home/SetupPage'));
const ComingSoon = lazy(() => import('../pages/ComingSoon'));
const WhatsNew = lazy(() => import('../pages/WhatsNew'));
const ProviderSupport = lazy(() => import('../pages/ProviderSupport'));
const ProvidersFAQ = lazy(() => import('../pages/ProvidersFAQ'));
const Onboarding = lazy(() => import('../modules/apps/clients/OnboardingPage'));
const LoadingPage = lazy(() => import('../modules/widgets/LoadingPage'));
const Intake = lazy(() => import('../modules/apps/clients/IntakePage'));
const Form = lazy(() => import('../modules/apps/clients/FormPage'));
const FormsPage = lazy(() => import('../modules/apps/forms/FormsPage'));
const FormBuilderPage = lazy(() => import('../modules/apps/forms/FormBuilderPage'));
const FormPreviewPage = lazy(() => import('../modules/apps/forms/FormPreviewPage'));
const LiveFormPreview = lazy(() => import('../modules/apps/forms/LiveFormPreview'));
const ClientBillingPage = lazy(() => import('../modules/apps/clients/ClientBillingPage'));
const SuperBillPage = lazy(() => import('../modules/apps/clients/SuperBillPage'));
const RescheduleRedirect = lazy(() => import('../modules/apps/clients/RescheduleRedirect'));
const Reschedule = lazy(() => import('../modules/apps/clients/ReschedulePage'));

const PrivateRoutes = () => {
  const { currentUser, currentProvider, currentClient, currentGuardian } = useAuth();
  const gates = roleGates(currentUser, currentProvider);
  // Clients and guardians cannot write through the provider workspaces (the API answers 403), so those routes
  // send them home. Their own /clients/<uuid>/* pages stay reachable.
  // The one client-user rule, `!currentProvider` (backend get_provider is None), read from currentUser.provider:
  // Auth derives currentProvider from it in an effect, one render late, which would bounce a provider's deep link.
  const clientUser = !!currentUser && !currentUser.provider;
  const providerOnly = (page: JSX.Element) => (clientUser ? <Navigate to="/home" /> : page);
  return (
    <Routes>
      <Route path="loading" element={<LoadingPage />} />
      <Route element={<MasterLayout />}>
        {/* Redirect to Dashboard after success login/registration */}
        <Route path="auth/*" element={<Navigate to="/home" />} />
        {/* Pages */}
        {currentProvider && currentProvider?.limited ? (
          <>
            <Route path="home" element={<Navigate to={`/clients/`} />} />
          </>
        ) : (
          <Route path="home" element={<HomePage />} />
        )}
        <Route path="setup" element={<SetupPage />} />
        <Route path="dashboard" element={<DashboardWrapper />} />
        <Route path="builder" element={<BuilderPageWrapper />} />
        <Route path="menu-test" element={<MenuTestPage />} />
        {/* Lazy Modules */}
        <Route
          path="crafted/pages/profile/*"
          element={
            <SuspensedView>
              <ProfilePage />
            </SuspensedView>
          }
        />
        <Route
          path="crafted/pages/wizards/*"
          element={
            <SuspensedView>
              <WizardsPage />
            </SuspensedView>
          }
        />
        <Route
          path="crafted/widgets/*"
          element={
            <SuspensedView>
              <WidgetsPage />
            </SuspensedView>
          }
        />
        <Route
          path="crafted/account/*"
          element={
            <SuspensedView>
              <AccountPage />
            </SuspensedView>
          }
        />
        <Route
          path="apps/chat/*"
          element={
            <SuspensedView>
              <ChatPage />
            </SuspensedView>
          }
        />
        <Route
          path="apps/user-management/*"
          element={
            <SuspensedView>
              <UsersPage />
            </SuspensedView>
          }
        />
        <Route
          path="services/*"
          element={
            <SuspensedView>
              <ServicesPage />
            </SuspensedView>
          }
        />
        <Route
          path="availability/*"
          element={
            <SuspensedView>
              <AvailabilityPage />
            </SuspensedView>
          }
        />
        {currentProvider && (
          <Route
            path="apps/*"
            element={
              <SuspensedView>
                <AppsHubPage />
              </SuspensedView>
            }
          />
        )}
        {/*<Route
          path="preferences/*"
          element={
            <SuspensedView>
              <PreferencesPage />
            </SuspensedView>
          }
        />*/}
        <Route
          path="clients/*"
          element={providerOnly(
            <SuspensedView>
              <ClientsPage />
            </SuspensedView>
          )}
        />
        <Route
          path="clients/:clientId/*"
          element={
            <SuspensedView>
              <ClientDetailsPage />
            </SuspensedView>
          }
        />
        <Route
          path="providers/*"
          element={
            <SuspensedView>
              <ProvidersPage />
            </SuspensedView>
          }
        />
        <Route
          path="client-provider/:clientProviderId/*"
          element={providerOnly(
            <SuspensedView>
              <ClientDetailsPage />
            </SuspensedView>
          )}
        />
        <Route
          path="providers/new/"
          element={
            // WST-1164: the Add Provider form is a Clinic Staff action; a direct visit without it lands on Home.
            gates.clinicStaff ? (
              <SuspensedView>
                <ProviderDetailsPage newProvider={true} />
              </SuspensedView>
            ) : (
              <Navigate to="/home" replace />
            )
          }
        />
        <Route
          path="providers/:providerId/*"
          element={
            <SuspensedView>
              <ProviderDetailsPage />
            </SuspensedView>
          }
        />
        <Route
          path="schedule/*"
          element={providerOnly(
            <SuspensedView>
              <SchedulePage />
            </SuspensedView>
          )}
        />
        <Route
          path="booking/*"
          element={
            currentUser?.provider && !gates.bookingApp ? (
              <Navigate to="/home" replace />
            ) : (
              <>
                {(gates.bookingApp || currentUser?.client || currentUser?.guardian) && (
                  <SuspensedView>
                    <BookingPage />
                  </SuspensedView>
                )}
              </>
            )
          }
        />
        <Route
          path="clinic/*"
          element={providerOnly(
            <SuspensedView>
              <ClinicPage />
            </SuspensedView>
          )}
        />
        <Route
          path="clinics/:clinicId/*"
          element={providerOnly(
            <SuspensedView>
              <ClinicPage />
            </SuspensedView>
          )}
        />
        <Route
          path="referrals/providers/*"
          element={
            <SuspensedView>
              <ReferralProvidersPage />
            </SuspensedView>
          }
        />
        <Route
          path="referrals/*"
          element={
            <SuspensedView>
              <ReferralsPageV2 />
            </SuspensedView>
          }
        />
        <Route
          path="referrals-legacy/*"
          element={
            <SuspensedView>
              <ReferralsLegacyPage />
            </SuspensedView>
          }
        />
        {currentProvider && (
          <>
          <Route
            path="documents/*"
            element={
              <SuspensedView>
                <ProviderDocuments />
              </SuspensedView>
            }
          />
          <Route
            path="documents/:docId"
            element={
              <SuspensedView>
                <ProviderDocuments />
              </SuspensedView>
            }
          />
          </>
        )}
        {currentProvider && (
          <>
            <Route
              path="forms"
              element={
                <SuspensedView>
                  <FormsPage />
                </SuspensedView>
              }
            />
            <Route
              path="forms/editor"
              element={
                gates.templates ? (
                  <SuspensedView>
                    <FormBuilderPage />
                  </SuspensedView>
                ) : (
                  <Navigate to="/documents" replace />
                )
              }
            />
            <Route
              path="forms/editor/:formTemplateId"
              element={
                gates.templates ? (
                  <SuspensedView>
                    <FormBuilderPage />
                  </SuspensedView>
                ) : (
                  <Navigate to="/documents" replace />
                )
              }
            />
            {/* preview routes moved outside MasterLayout for full-screen rendering */}
          </>
        )}
        {currentClient && (
          <>
            <Route
              path="documents/*"
              element={
                <SuspensedView>
                  <ClientDocuments client={currentClient} />
                </SuspensedView>
              }
            />
            <Route
              path="documents/:docId"
              element={
                <SuspensedView>
                  <ClientDocuments client={currentClient} />
                </SuspensedView>
              }
            />
            <Route
              path="notes/*"
              element={
                <SuspensedView>
                  <ClientNotes client={currentClient} />
                </SuspensedView>
              }
            />
            <Route
              path="notes/:noteId"
              element={
                <SuspensedView>
                  <ClientNotes client={currentClient} />
                </SuspensedView>
              }
            />
            <Route
              path="clients/:clientId/contact/:contactId/consent/:consentId"
              element={
                <SuspensedView>
                  <ClientContactInfo />
                </SuspensedView>
              }
            />
          </>
        )}
        {currentGuardian && (
          <>
            <Route
              path="documents/*"
              element={
                <SuspensedView>
                  {currentGuardian.clients && currentGuardian.clients.length > 0 ? (
                    currentGuardian.clients?.map((c) => (
                      <>
                        <h1 className="my-5 mb-10">{c.full_name}</h1>
                        <ClientDocuments client={c} />
                      </>
                    ))
                  ) : (
                    <h1>No Clients</h1>
                  )}
                </SuspensedView>
              }
            />
            <Route
              path="notes/*"
              element={
                <SuspensedView>
                  {currentGuardian.clients && currentGuardian.clients.length > 0 ? (
                    currentGuardian.clients?.map((c) => (
                      <>
                        <h1 className="my-5">{c.full_name}</h1>
                        <ClientNotes client={c} />
                      </>
                    ))
                  ) : (
                    <h1>No Clients</h1>
                  )}
                </SuspensedView>
              }
            />
            <Route
              path="notes/:noteId"
              element={
                <SuspensedView>
                  {currentGuardian.clients && currentGuardian.clients.length > 0 ? (
                    currentGuardian.clients?.map((c) => (
                      <>
                        <h1 className="my-5">{c.full_name}</h1>
                        <ClientNotes client={c} />
                      </>
                    ))
                  ) : (
                    <h1>No Clients</h1>
                  )}
                </SuspensedView>
              }
            />
            <Route
              path="clients/:clientId/contact/:contactId/consent/:consentId"
              element={
                <SuspensedView>
                  <ClientContactInfo />
                </SuspensedView>
              }
            />
          </>
        )}

        {/*<Route
          path="integrations/*"
          element={
            <SuspensedView>
              <IntegrationsPage />
            </SuspensedView>
          }
        />*/}
        <Route
          path="ComingSoon/*"
          element={
            <SuspensedView>
              <ComingSoon />
            </SuspensedView>
          }
        />
        <Route
          path="WhatsNew/*"
          element={
            <SuspensedView>
              <WhatsNew />
            </SuspensedView>
          }
        />
        <Route
          path="ProviderSupport/*"
          element={
            <SuspensedView>
              <ProviderSupport />
            </SuspensedView>
          }
        />
        <Route
          path="ProvidersFAQ/*"
          element={
            <SuspensedView>
              <ProvidersFAQ />
            </SuspensedView>
          }
        />
        <Route
          path="billing/*"
          element={
            <SuspensedView>
              <ClientBillingPage />
            </SuspensedView>
          }
        />
        <Route
          path="superbill/:superBillId"
          element={
            <SuspensedView>
              <SuperBillPage />
            </SuspensedView>
          }
        />
        <Route
          path="financials/*"
          element={
            currentUser?.provider && !currentUser?.provider?.is_admin && !currentUser?.provider?.payment_details_enabled
              ? <Navigate to="/home" />
              : <SuspensedView><FinancialsPage /></SuspensedView>
          }
        />
        <Route
          path="reports/*"
          element={
            currentUser?.provider && !gates.reports
              ? <Navigate to="/home" />
              : <SuspensedView><ReportsPage /></SuspensedView>
          }
        />
        {/* <Route
          path="reschedule/:token/"
          element={
            <SuspensedView>
              <RescheduleRedirect />
            </SuspensedView>
          }
        /> */}
        <Route path="reschedule/:token/" element={<Reschedule />} />
        <Route path="m/:token/" element={<Reschedule />} />

        {/* Page Not Found */}
      </Route>
      <Route path="forms/preview/:formTemplateId" element={<FormPreviewPage />} />
      <Route path="forms/preview-live" element={<LiveFormPreview />} />
      <Route path="i/:inviteId" element={<Onboarding />} />
      <Route path="onboarding/:inviteId" element={<Onboarding />} />
      <Route path="onboarding/" element={<Onboarding />} />
      <Route path="form/:formId" element={<Form />} />
      <Route path="/:formId/:inviteId" element={<Intake />} />
      <Route path="/:formId/:inviteId/" element={<Intake />} />
      <Route path="/:formId/" element={<Intake />} />
      <Route path="*" element={<Navigate to="/error/404" />} />
    </Routes>
  );
};

const SuspensedView: FC = ({ children }) => {
  const baseColor = getCSSVariableValue('--bs-primary');
  TopBarProgress.config({
    barColors: {
      '0': baseColor,
    },
    barThickness: 1,
    shadowBlur: 5,
  });
  return <Suspense fallback={<TopBarProgress />}>{children}</Suspense>;
};

export { PrivateRoutes };
